import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { IconCopy } from '@tabler/icons-react'
import { useAppDispatch, useAppSelector } from 'store'
import styled from 'styled-components'

import { Button } from 'components/atoms/Button'
import { getPartnersByCurrentCountryAndBrand } from 'services/partnersSlice'
import { cacheProduct,selectCachedProduct  } from 'services/productsSlice'
import { IPartner } from 'types/partners'
import { IEditProduct } from 'types/products'
import { TRANSLATION_STATUS, TranslationProgress } from 'types/translations'
import { isNotNilOrEmpty } from 'utils/ramda'
import { getDefaultTemplate } from 'utils/templateUtils'

interface TranslationProgressContentProps {
  translationProgress: TranslationProgress
  isBatch?: boolean
  productData?: IEditProduct | null
  displayName?: string
}

export const TranslationProgressContent: React.FC<
  TranslationProgressContentProps
> = ({ translationProgress, isBatch = false, productData, displayName }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Get cached translated product if needed
  const cachedTranslatedProduct = useAppSelector(
    selectCachedProduct(translationProgress?.translatedProductId || '')
  )

  const getStatusMessage = (status: string) => {
    switch (status) {
      case TRANSLATION_STATUS.pending:
        return 'Waiting to start...'
      case TRANSLATION_STATUS.validating:
        return 'Validating data...'
      case TRANSLATION_STATUS.translating:
        return 'Translating with AI...'
      case TRANSLATION_STATUS.translating_assets:
        return 'Translating assets...'
      case TRANSLATION_STATUS.finalizing:
        return 'Finalizing translation...'
      case TRANSLATION_STATUS.completed:
        return 'Translation completed successfully!'
      case TRANSLATION_STATUS.failed:
        return 'Translation failed'
      case TRANSLATION_STATUS.copying_files:
        return 'Copying files...'
      case TRANSLATION_STATUS.creating_product:
        return 'Creating translated product...'
      default:
        return 'Processing...'
    }
  }

  const handleNavigateToProducts = () => {
    try {
      const targetCountry = translationProgress?.targetCountry

      if (targetCountry) {
        navigate(
          `/products?country=${targetCountry}&orderBy=createdAt&orderDir=desc`
        )
      } else {
        navigate('/products')
      }
    } catch (error) {
      console.error('Error navigating to products:', error)
    }
  }

  const handleNavigateToTranslatedProduct = () => {
    try {
      const productId =
        translationProgress?.translatedProductId ||
        translationProgress?.productId

      if (productId) {
        navigate(`/product-edit?productId=${productId}`)
      }
    } catch (error) {
      console.error('Error navigating to translated product:', error)
    }
  }

  const handleOpenPreview = async () => {
    // Preview shows the translated product in the target country
    const productId =
      translationProgress?.translatedProductId || translationProgress?.productId
    const targetCountry = translationProgress?.targetCountry

    if (!productId || !targetCountry) return

    try {
      // Ensure translated product is cached when we're previewing a translation
      if (
        translationProgress?.translatedProductId &&
        productId !== translationProgress?.productId &&
        !cachedTranslatedProduct
      ) {
        await dispatch(cacheProduct(productId))
      }

      // Use productData (source or cached product) for brand/category; same brand as translated product
      const productForTemplate = productData
      const brandName =
        productForTemplate?.productCategory?.brand?.name ?? ''
      const categoryName = productForTemplate?.productCategory?.name

      let partnerName = 'Max Elektro'
      let templateName = 'default'

      if (brandName) {
        const defaultTemplate = getDefaultTemplate(
          brandName,
          categoryName,
          productForTemplate?.name
        )

        templateName = defaultTemplate.id

        // Prefer partners for target country; if none (e.g. RO has no partners yet), use source country (e.g. PL)
        let partnersAction = await dispatch(
          getPartnersByCurrentCountryAndBrand({
            country: targetCountry,
            brandName
          })
        )
        let partners: IPartner[] =
          partnersAction.meta.requestStatus === 'fulfilled'
            ? (partnersAction.payload as IPartner[])
            : []

        let fromSourceCountry = false

        if (!isNotNilOrEmpty(partners) && translationProgress?.sourceCountry) {
          partnersAction = await dispatch(
            getPartnersByCurrentCountryAndBrand({
              country: translationProgress.sourceCountry,
              brandName
            })
          )
          partners =
            partnersAction.meta.requestStatus === 'fulfilled'
              ? (partnersAction.payload as IPartner[])
              : []
          fromSourceCountry = true
        }

        if (isNotNilOrEmpty(partners)) {
          if (fromSourceCountry) {
            const maxElektro = partners.find(
              p => p.name === 'Max Elektro'
            )

            partnerName = maxElektro ? maxElektro.name : partners[0].name
          } else {
            partnerName = partners[0].name
          }
        }
      }

      const previewUrl = `/card-preview?productId=${productId}&partnerName=${encodeURIComponent(
        partnerName
      )}&templateName=${templateName}&country=${targetCountry}`

      window.open(previewUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Failed to fetch product for preview:', error)
      const previewUrl = `/card-preview?productId=${productId}&partnerName=Max%20Elektro&templateName=default&country=${targetCountry}`

      window.open(previewUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const isInProgress = [
    TRANSLATION_STATUS.pending,
    TRANSLATION_STATUS.validating,
    TRANSLATION_STATUS.translating,
    TRANSLATION_STATUS.copying_files,
    TRANSLATION_STATUS.creating_product,
    TRANSLATION_STATUS.translating_assets,
    TRANSLATION_STATUS.finalizing
  ].includes(translationProgress.status)

  const isCompleted =
    translationProgress.status === TRANSLATION_STATUS.completed

  const isFailed = translationProgress.status === TRANSLATION_STATUS.failed

  return (
    <Content>
      <StatusSection>
        <StatusText $color={'#000000'}>
          {getStatusMessage(translationProgress.status)}
        </StatusText>
        {!isFailed && (
          <ProgressContainer>
            <ProgressBar>
              <ProgressFill $progress={translationProgress.progress} />
            </ProgressBar>
            <ProgressText>{translationProgress.progress}%</ProgressText>
          </ProgressContainer>
        )}
      </StatusSection>

      <DetailsSection>
        {isBatch ? (
          <>
            {productData?.productCategory?.brand?.name && (
              <DetailRow>
                <DetailLabel>Brand:</DetailLabel>
                <DetailValue>
                  {productData.productCategory.brand.name}
                </DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>Products:</DetailLabel>
              <DetailValue>
                {translationProgress.productId
                  .replace('batch_', '')
                  .replace('_products', '')}{' '}
                products
              </DetailValue>
            </DetailRow>
          </>
        ) : (
          <>
            {productData?.productCategory?.brand?.name && (
              <DetailRow>
                <DetailLabel>Brand:</DetailLabel>
                <DetailValue>
                  {productData.productCategory.brand.name}
                </DetailValue>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>Product:</DetailLabel>
              <DetailValue>
                {displayName || translationProgress.productId}
              </DetailValue>
            </DetailRow>
            {productData?.sku && (
              <DetailRow>
                <DetailLabel>SKU:</DetailLabel>
                <DetailValue>{productData.sku}</DetailValue>
              </DetailRow>
            )}
          </>
        )}
        <DetailRow>
          <DetailLabel>From:</DetailLabel>
          <DetailValue>{translationProgress.sourceCountry}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>To:</DetailLabel>
          <DetailValue>{translationProgress.targetCountry}</DetailValue>
        </DetailRow>
        {translationProgress.totalAssets !== undefined &&
          translationProgress.totalAssets !== null &&
          translationProgress.totalAssets > 0 && (
            <DetailRow>
              <DetailLabel>{isBatch ? 'Completed:' : 'Assets:'}</DetailLabel>
              <DetailValue>
                {translationProgress.translatedAssetCount || 0} /{' '}
                {translationProgress.totalAssets}
              </DetailValue>
            </DetailRow>
          )}
        {translationProgress.fromCache && (
          <DetailRow>
            <DetailLabel>Source:</DetailLabel>
            <DetailValue style={{ color: '#1976d2', fontWeight: '500' }}>
              <IconCopy
                size={14}
                style={{ marginRight: '4px', display: 'inline' }}
              />
              Shared from another user's translation
            </DetailValue>
          </DetailRow>
        )}
        {isFailed && translationProgress.error && (
          <ErrorDetailRow>
            <DetailLabel>Failure cause:</DetailLabel>
            <ErrorDetailValue>{translationProgress.error}</ErrorDetailValue>
          </ErrorDetailRow>
        )}
      </DetailsSection>
      {isInProgress && (
        <LoadingSection>
          <CircularProgress size={16} />
          <LoadingText>
            {isBatch
              ? 'Processing batch translation...'
              : 'Processing translation...'}
          </LoadingText>
        </LoadingSection>
      )}
      {isCompleted && (
        <ActionSection>
          {isBatch ? (
            <StyledButtonWithHover>
              <Button
                variant='outlined'
                size='large'
                onClick={handleNavigateToProducts}
              >
                Product List
              </Button>
            </StyledButtonWithHover>
          ) : (
            <>
              <StyledButtonWithHover>
                <Button
                  variant='outlined'
                  size='large'
                  onClick={handleNavigateToTranslatedProduct}
                >
                  Edit
                </Button>
              </StyledButtonWithHover>
              <StyledButtonWithHover>
                <Button
                  variant='outlined'
                  size='large'
                  onClick={handleOpenPreview}
                >
                  Preview
                </Button>
              </StyledButtonWithHover>
              <StyledButtonWithHover>
                <Button
                  variant='outlined'
                  size='large'
                  onClick={handleNavigateToProducts}
                >
                  Product List
                </Button>
              </StyledButtonWithHover>
            </>
          )}
        </ActionSection>
      )}
    </Content>
  )
}

const Content = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
`

const StatusSection = styled.div`
  margin-bottom: 16px;
`

const StatusText = styled.div<{ $color: string }>`
  font-size: ${({ theme }) => theme.typography.fontSizeNormal};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ $color }) => $color};
  margin-bottom: 8px;
`

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: ${({ theme }) => theme.colors.gray};
  border-radius: 3px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: #814f99;
  width: ${({ $progress }) => $progress}%;
  transition: width 0.3s ease;
  border-radius: 3px;
`

const ProgressText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizeSmall};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: #814f99;
  min-width: 32px;
`

const DetailsSection = styled.div`
  margin-bottom: 16px;
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: ${({ theme }) => theme.typography.fontSizeSmall};
`

const DetailLabel = styled.div`
  color: ${({ theme }) => theme.colors.muiLabel};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`

const DetailValue = styled.div`
  color: ${({ theme }) => theme.colors.menuLabel};
  font-weight: ${({ theme }) => theme.typography.fontWeightRegular};
  text-align: right;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ErrorDetailRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  font-size: ${({ theme }) => theme.typography.fontSizeSmall};
`

const ErrorDetailValue = styled.div`
  color: #d32f2f;
  font-weight: ${({ theme }) => theme.typography.fontWeightRegular};
  text-align: left;
  margin-top: 4px;
  word-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
`

const LoadingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 8px;
  background: ${({ theme }) => theme.colors.primarySuperLight};
  border-radius: 6px;
`

const LoadingText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizeSmall};
  color: #814f99;
`

const ActionSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`

const StyledButtonWithHover = styled.div`
  button {
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primary} !important;
      border-color: ${({ theme }) => theme.colors.primary} !important;
      color: white !important;

      svg {
        color: white !important;
      }
    }
  }
`
